import { message } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import usePostData from "../../../api/usePostData";
import { saveUserData } from "../../../redux/slices/authSlice";
import { RegisterDone } from "../../../utils/mixpanel";
import { getRemainingExpiration } from "../../../utils/getOTPExpirationTime";

export const AccountInfoContext = React.createContext({
  email: "",
  firstStepSubmit: () => {},
  unsetEmail: () => {},
  otpDone: false,
  existEmail: false,
  finishOTP: () => {},
  phone: "",
  phoneStepSubmit: () => {},
  recaptchaToken: "",
  recaptchaRef: null,
  setRecaptchaToken: () => {},
  setExistEmail: () => {},
  timer: 0,
  setTimer: () => {},
});

/**
 * A provider that is used to wrap the data of account info step.
 * It stores the following values:
 *  - email: The email the user has entered
 *  - otpDone: Whether the user has entered the OTP successfully or not.
 *  - recaptchaToken: The recpatcha token given from google api when the user is successfull in solving the recaptcha. It gets stored so that it can be sent in the register request.
 *  - recaptchaRef: A React reference to the google recpatcha UI component. It is stored so that it can be reset if there are any errors from the backend in the registeration request.
 *  - timer: The time remaining to send OTP
 * It also returns the following actions:
 *  - firstStepSubmit: A function to be called when the user finishes the first step, the one with the email.
 *  - finishOTP: A function to be called to mark that the user has finished the otpStep
 *  - phoneStepSubmit: A function to be called when the user submits their phone number. It sends a request to the registeration endpoint
 *  - unsetEmail: A function that unsets the email to return the user to the first step
 *  - setRecaptchaToken: A function that sets the recpatcha token obtained from google api when the user solves the recpatcha successfully
 *  - setTimer: A setter for the timer state
 *
 * @param {Object} props
 * @param {JSX.Element | JSX.Element[]} props.children - The elements inside the context provider
 * @returns {JSX.Element}
 */

export const AccountInfoProvider = ({ children }) => {
  const [email, setEmail] = useState("");
  const [otpDone, setOTPDone] = useState(false);
  const [existEmail, setExistEmail] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [timer, setTimer] = useState(getRemainingExpiration());
  const recaptchaRef = useRef(null);
  const { postData: registerUser, errors: registerationErrors } = usePostData(
    {}
  );
  const firstStepSubmit = (email) => {
    setEmail(email);
  };
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const finishOTP = () => setOTPDone(true);
  const phoneStepSubmit = async (phone) => {
    const res = await registerUser({
      route: "api/method/airport_transport.api.user.register",
      data: {
        email,
        phone,
      },
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (res.data?.data) {
      const { token, ...userData } = res.data.data;
      dispatch(
        saveUserData({
          token,
          userData: {
            username: userData.name,
            email: userData.email,
          },
        })
      );
      RegisterDone(userData.name, userData.email, phone);
    }
  };
  useEffect(() => {
    if (registerationErrors) {
      const errorMsg = registerationErrors?.error || "";
      if (errorMsg.toLowerCase() === "mobile number must be unique") {
        message.error(t("errors.mobile_number_must_be_unique"));
      } else {
        message.error(`${errorMsg}`);
      }
      setRecaptchaToken(null);
      recaptchaRef?.current?.reset();
    }
  }, [registerationErrors]);
  const unsetEmail = () => setEmail("");
  return (
    <AccountInfoContext.Provider
      value={{
        email,
        firstStepSubmit,
        otpDone,
        existEmail,
        setExistEmail,
        timer,
        finishOTP,
        phoneStepSubmit,
        unsetEmail,
        recaptchaToken,
        setRecaptchaToken,
        setTimer,
        recaptchaRef,
      }}
    >
      {children}
    </AccountInfoContext.Provider>
  );
};
